<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { notify } from '@kyvg/vue3-notification'
import axios from 'axios'

import { useAuthStore } from '@/stores/auth-store'
import useForm from '@/hooks/use-form'
import { LoadingListRoute } from '@/types/cargo-loading'
import { ResourceResponse } from '@/types/general'
import { formatRouteInfo } from '@/utils/cargo-loading'

import MyModal from '@/components/my-components/MyModal.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MyInput from '@/components/my-components/form/MyInput.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyErrorMessage from '@/components/my-components/form/MyErrorMessage.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'

interface Form {
    routeACarrierNumber: string
    routeBCarrierNumber: string
    routeBItems: string[]
}

interface Props {
    routeId: string
    modelValue: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
    (e: 'close'): void
    (e: 'saved'): void
}>()

const { t } = useI18n()
const authStore = useAuthStore()

const { data, submit, loading, errors, reset } = useForm<Form>({
    routeACarrierNumber: '',
    routeBCarrierNumber: '',
    routeBItems: [],
})

const route = ref<LoadingListRoute | null>(null)
const fetchingRoute = ref(false)

const sortedItems = computed(() => {
    if (!route.value?.items) return []

    return [...route.value.items].sort((itemA, itemB) => {
        const last4A = parseInt(itemA.awbNumber.slice(-4), 10)
        const last4B = parseInt(itemB.awbNumber.slice(-4), 10)
        return last4A - last4B
    })
})

const routeAItems = computed(() =>
    sortedItems.value.filter((item) => !data.routeBItems.includes(item.id)),
)

const routeBItems = computed(() =>
    sortedItems.value.filter((item) => data.routeBItems.includes(item.id)),
)

async function fetchRoute() {
    fetchingRoute.value = true
    try {
        const response = await axios.get<ResourceResponse<LoadingListRoute>>(
            window.route('cl.company.loading-list-routes.show', {
                company: authStore.companyId,
                loading_list_route: props.routeId,
            }),
        )
        route.value = response.data.data
        initializeRouteNames()
    } finally {
        fetchingRoute.value = false
    }
}

function initializeRouteNames() {
    if (route.value?.carrierNumber) {
        const carrierNumber = route.value.carrierNumber.trim()

        // Always add A and B suffixes to the carrier number
        data.routeACarrierNumber = `${carrierNumber} A`
        data.routeBCarrierNumber = `${carrierNumber} B`
    }
    data.routeBItems = []
}

function onToggleItem(itemId: string) {
    const index = data.routeBItems.indexOf(itemId)
    if (index > -1) {
        data.routeBItems.splice(index, 1)
    } else {
        data.routeBItems.push(itemId)
    }
}

function routeInfo(carrierNumber: string): string {
    if (!route.value) return carrierNumber

    return formatRouteInfo(route.value.destinationPort.shortName, {
        ...route.value,
        carrierNumber,
    })
}

function onOpen() {
    if (!props.modelValue) return

    reset()

    if (props.routeId) {
        fetchRoute()
    }
}

function onClose() {
    emit('close')
    reset()
}

async function onSubmit() {
    if (!route.value?.id) return

    const response = await submit(
        'PUT',
        window.route('cl.company.loading-list-routes.split', {
            company: authStore.companyId,
            loading_list_route: route.value.id,
        }),
    )

    if (response) {
        notify({
            title: t('success'),
            text: t('routeSplitSuccessfully'),
            type: 'success',
        })
        emit('saved')
        onClose()
    }
}

onBeforeMount(onOpen)

watch(() => props.modelValue, onOpen)
</script>

<template>
    <MyModal :model-value="modelValue" :max-width="800" @close="onClose">
        <LoaderWrapper :visible="loading || fetchingRoute" class="rounded-xl" />
        <template #title>
            {{ t('splitRoute') }}
        </template>
        <MyForm :errors="errors" class="p-6" @submit.prevent="onSubmit">
            <div class="grid grid-cols-2 gap-6 mb-6">
                <MyInput
                    v-model="data.routeACarrierNumber"
                    name="routeACarrierNumber"
                    :label="t('routeA')"
                    :placeholder="t('routeAName')"
                />
                <MyInput
                    v-model="data.routeBCarrierNumber"
                    name="routeBCarrierNumber"
                    :label="t('routeB')"
                    :placeholder="t('routeBName')"
                />
            </div>

            <div class="grid grid-cols-2 gap-6 mb-6">
                <div class="border rounded-lg p-4">
                    <h3 class="font-medium mb-3 text-center">
                        {{ routeInfo(data.routeACarrierNumber) }}
                    </h3>

                    <div class="space-y-2 max-h-60 overflow-y-auto">
                        <MyButton
                            v-for="item in routeAItems"
                            :key="item.id"
                            scheme="light"
                            size="medium"
                            class="relative w-full flex items-center justify-center border shadow-sm px-12 py-3 !bg-stone-200 dark:!bg-dark-500 !text-stone-800 dark:!text-stone-100 hover:!bg-stone-300 dark:hover:!bg-dark-400"
                            @click="onToggleItem(item.id)"
                        >
                            <span class="text-sm font-mono">{{ item.awbNumber }}</span>

                            <mdi:arrow-right-thin
                                class="absolute right-4 h-5 w-5 opacity-80 pointer-events-none"
                            />
                        </MyButton>
                        <div
                            v-if="routeAItems.length === 0"
                            class="text-center text-stone-500 text-sm py-4"
                        >
                            {{ t('noItemsFound') }}
                        </div>
                    </div>
                </div>

                <div class="border rounded-lg p-4">
                    <h3 class="font-medium mb-3 text-center">
                        {{ routeInfo(data.routeBCarrierNumber) }}
                    </h3>

                    <div class="space-y-2 max-h-60 overflow-y-auto">
                        <MyButton
                            v-for="item in routeBItems"
                            :key="item.id"
                            scheme="primary"
                            size="medium"
                            class="relative w-full flex items-center justify-center border shadow-sm px-12 py-3"
                            @click="onToggleItem(item.id)"
                        >
                            <mdi:close-thick
                                class="absolute left-4 h-4 w-4 opacity-90 pointer-events-none"
                            />

                            <span class="text-sm font-mono">{{ item.awbNumber }}</span>
                        </MyButton>
                        <div
                            v-if="routeBItems.length === 0"
                            class="text-center text-stone-500 text-sm py-4"
                        >
                            {{ t('noItemsFound') }}
                        </div>
                    </div>
                </div>
            </div>

            <MyErrorMessage input-name="routeBItems" />
            <div class="mt-4 flex justify-end gap-6">
                <MyButton :disabled="loading" scheme="primary" type="submit">
                    {{ t('splitRoute') }}
                </MyButton>
            </div>
        </MyForm>
    </MyModal>
</template>
